<!--
  PromptModal.vue
  프롬프트 추가/수정 모달 컴포넌트 
-->
<template>
  <a-modal
    :open="open"
    :title="isEdit ? '프롬프트 수정' : '프롬프트 추가'"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    width="1000px"
  >
    <div class="modal-form">
      <div class="form-row">
        <div class="form-group">
          <label>프롬프트 코드 <span class="required">*</span></label>
          <a-input
            v-model:value="form.promptCode"
            placeholder="프롬프트 코드를 입력하세요 (예: MEDICAL_REVIEW_001)"
            @input="handlePromptCodeInput"
            style="text-transform: uppercase;"
          />
        </div>
        
        <div class="form-group">
          <label>프롬프트명 <span class="required">*</span></label>
          <a-input
            v-model:value="form.promptName"
            placeholder="프롬프트명을 입력하세요"
          />
        </div>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label>프롬프트 타입 <span class="required">*</span></label>
          <a-select v-model:value="form.promptType" style="width: 100%">
            <a-select-option value="medical_review">의료 후기</a-select-option>
            <a-select-option value="product_review">제품 후기</a-select-option>
            <a-select-option value="service_review">서비스 후기</a-select-option>
          </a-select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group full-width">
          <label>시스템 프롬프트 <span class="required">*</span></label>
          <a-textarea
            v-model:value="form.systemPrompt"
            placeholder="시스템 프롬프트를 입력하세요"
            :rows="15"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group full-width">
          <label>사용자 프롬프트 <span class="required">*</span></label>
          <a-textarea
            v-model:value="form.userPrompt"
            placeholder="사용자 프롬프트를 입력하세요"
            :rows="4"
          />
        </div>
      </div>
      
      <!-- 自定义按钮区域 -->
      <div class="modal-footer">
        <a-button @click="handleCancel">
          취소
        </a-button>
        <a-button 
          type="primary" 
          class="yellow-button"
          :loading="loading"
          @click="handleOk"
        >
          {{ isEdit ? '수정' : '추가' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { reactive, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  name: 'PromptModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    promptData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    // 폼 데이터
    const form = reactive({
      promptCode: '',
      promptName: '',
      promptType: '',
      userPrompt: '',
      systemPrompt: ''
    });

    // 폼 유효성 검사
    const validateForm = () => {
      if (!form.promptCode.trim()) {
        message.warning('프롬프트 코드를 입력해주세요.');
        return false;
      }
      
      if (!form.promptName.trim()) {
        message.warning('프롬프트명을 입력해주세요.');
        return false;
      }
      
      if (!form.promptType) {
        message.warning('프롬프트 타입을 선택해주세요.');
        return false;
      }
      
      if (!form.userPrompt.trim()) {
        message.warning('사용자 프롬프트를 입력해주세요.');
        return false;
      }
      
      if (!form.systemPrompt.trim()) {
        message.warning('시스템 프롬프트를 입력해주세요.');
        return false;
      }
      
      return true;
    };

    // 폼 초기화
    const resetForm = () => {
      form.promptCode = '';
      form.promptName = '';
      form.promptType = '';
      form.userPrompt = '';
      form.systemPrompt = '';
    };

    // 폼 데이터 설정
    const setFormData = (data) => {
      if (data && Object.keys(data).length > 0) {
        form.promptCode = data.promptCode || '';
        form.promptName = data.promptName || '';
        form.promptType = data.promptType || '';
        form.userPrompt = data.userPrompt || '';
        form.systemPrompt = data.systemPrompt || '';
      }
    };

    // 확인 버튼 클릭
    const handleOk = () => {
      if (!validateForm()) return;
      
      emit('ok', { ...form });
    };

    // 취소 버튼 클릭
    const handleCancel = () => {
      resetForm();
      emit('cancel');
    };

    // props 변경 감지 - promptData와 isEdit 상태에 따라 폼 데이터 설정
    watch([() => props.promptData, () => props.isEdit, () => props.open], ([newData, isEdit, open]) => {
      if (open && isEdit && newData && Object.keys(newData).length > 0) {
        // 수정 모드이고 모달이 열렸을 때 데이터 설정
        setFormData(newData);
      } else if (open && !isEdit) {
        // 추가 모드이고 모달이 열렸을 때 폼 초기화
        resetForm();
      } else if (!open) {
        // 모달이 닫혔을 때 폼 초기화
        resetForm();
      }
    }, { immediate: true, deep: true });

    // 프롬프트 코드 입력 핸들러
    const handlePromptCodeInput = (event) => {
      form.promptCode = event.target.value.toUpperCase();
    };

    return {
      form,
      handleOk,
      handleCancel,
      resetForm,
      setFormData,
      handlePromptCodeInput
    };
  }
};
</script>

<style scoped lang="scss">
// 모달 폼
.modal-form {
  padding: 20px;
  .form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .form-group {
    flex: 1;
    
    &.full-width {
      flex: none;
      width: 100%;
    }
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #262626;
      
      .required {
        color: #ff4d4f;
        margin-left: 4px;
      }
    }
    
    :deep(.ant-input),
    :deep(.ant-select),
    :deep(.ant-textarea) {
      border-radius: 6px;
    }
  }
}

// 모달 푸터
.modal-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 24px 0 0 0;
  margin-top: 24px;
  border-top: 1px solid #f0f0f0;
}

// 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .modal-footer {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
}
</style> 